import { Button, DatePicker, Form, Input, message, Select } from "antd";
import dayjs from "dayjs";
import { useEffect, useState, useCallback } from "react";
import { SiMicrosoftexcel } from "react-icons/si";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as XLSX from "xlsx";
import Loading from "../../components/loading";
import API from "../../config/api";
import { DELETE, GET, PUT } from "../../utils/apiCalls";
import PageHeader from "../routes/pageHeader";
import DataTable from "./components/dataTable";
import Forms from "./components/form";
import UploadModal from "./components/uploadModal";
import "./styles.scss";

function StartupScreen() {
  const Events = useSelector((state: any) => state.Events);
  const user = useSelector((state: any) => state.User.user);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState({});
  const [selectedItem, setSelectedItem] = useState({});
  const [openForm, setOpenForm] = useState(false);
  const [formModal, setFormModal] = useState(false);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, [searchParams]);

  const fetchData = useCallback(async () => {
    setIsLoadingData(true);
    try {
      const params = {
        ...(searchParams.get("query") && { query: searchParams.get("query") }),
        ...(searchParams.get("startDate") && {
          startDate: searchParams.get("startDate"),
        }),
        ...(searchParams.get("page") && { page: searchParams.get("page") }),
        ...(searchParams.get("limit") && { take: searchParams.get("limit") }),
        ...(searchParams.get("attended") && { attended: searchParams.get("attended") }),
        ...(searchParams.get("sortField") && { sortField: searchParams.get("sortField") }),
        ...(searchParams.get("sortOrder") && { sortOrder: searchParams.get("sortOrder") }),
        eventId: Events?.defult?.id,
      };
      const res: any = await GET(API.STARTUPS_LIST_SEARCH, params);
      if (res.status) {
        setData(res.data);
        setMeta(res.meta);
      } else {
        message.error("Failed to fetch startup data.");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("An error occurred while fetching data.");
    } finally {
      setIsLoadingData(false);
      setLoading(false);
    }
  }, [Events?.defult?.id, searchParams]);

  const handleEditItem = (item: any) => {
    setSelectedItem(item);
    setOpenForm(true);
  };

  const handleDeleteItem = async (id: any) => {
    try {
      const response: any = await DELETE(`${API.STARTUPS_DELETE}${id}`);
      if (response?.status) {
        fetchData();
        message.success("Successfully deleted.");
      } else {
        message.error("An error occurred while deleting.");
      }
    } catch {
      message.error("An error occurred while deleting.");
    }
  };

  const updateAttendance = async (data: {
    id: string;
    has_attended: boolean;
    startup_id: string;
  }) => {
    try {
      // Check if id (uuid) exists
      if (!data.id || data.id === 'undefined') {
        message.error("Unable to update attendance: Missing identifier");
        console.error("Missing UUID for attendance update:", data);
        return;
      }

      const response: any = await PUT(
        `${API.STARTUPS_UPDATE_ATTENDANCE}${data.id}`,
        { has_attended: data?.has_attended, startup_id: data?.startup_id, updated_by: user?.name }
      );
      if (response?.status) {
        fetchData();
        message.success("Attendance updated successfully.");
      } else {
        message.error("Failed to update.");
      }
    } catch (err) {
      console.error("Error updating attendance:", err);
      message.error("An error occurred while updating attendance.");
    }
  };

  const handleGenerateExcel = async () => {
    setIsGeneratingExcel(true);
    try {
      const params = {
        ...(searchParams.get("query") && { query: searchParams.get("query") }),
        ...(searchParams.get("startDate") && {
          startDate: searchParams.get("startDate"),
        }),
        ...(searchParams.get("attended") && { attended: searchParams.get("attended") }),
        // Add sorting parameters for excel generation
        ...(searchParams.get("sortField") && { sortField: searchParams.get("sortField") }),
        ...(searchParams.get("sortOrder") && { sortOrder: searchParams.get("sortOrder") }),
        eventId: Events?.defult?.id,
        // Don't include page and take for Excel generation to get all records
      };
      
      // Use the same API endpoint as fetchData to support sorting
      const response: any = await GET(API.STARTUPS_LIST_SEARCH, params);
      
      if (response?.status) {
        const exportData = response.data.map((item: any, index: number) => ({
          No: index + 1,
          Name: item.name,
          Sector: item.category,
          Founder: item.founder,
          Email: item.email,
          Country: item.country,
          Stage: item.Stage,
          "Fund Raised": item.fund_raised,
          "Notable Investors": item.notable_inverstors,
          Attendance: item.has_attended ? "Attended" : "Not Attended",
          "Created At": dayjs(item.createdAt).format("DD MMM YYYY"),
          "Updated At": dayjs(item.updatedAt).format("DD MMM YYYY"),
          "Profile Link": {
            t: "l",
            v: `${API.WEB_URL}/startup-details/${item.uuid}`,
            l: { Target: `${API.WEB_URL}/startup-details/${item.uuid}` }
          }
        }));
        
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        // Auto-fit columns
        const colWidths = Object.keys(exportData[0]).map(key => ({
          wch: Math.max(
            key.length,
            ...exportData.map((row: any) => (row[key] ? row[key].toString().length : 0))
          )
        }));
        worksheet["!cols"] = colWidths;
        
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Startups");
        
        const excelBuffer = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });
        
        const dataBlob = new Blob([excelBuffer], {
          type: "application/octet-stream",
        });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(dataBlob);
        link.download = `Startups-List-${new Date().toLocaleString()}.xlsx`;
        link.click();
      } else {
        message.error("Couldn't generate the report.");
      }
    } catch (error) {
      console.error("Error generating report:", error);
      message.error("An error occurred while generating the report.");
    } finally {
      setIsGeneratingExcel(false);
    }
  };
  
  // Updated handleSearchParamsChange - properly preserve sorting during filtering
  const handleSearchParamsChange = (data: any) => {
    const params: any = {
      page: data?.page || searchParams.get("page") || "1",
      limit: data?.pageSize || searchParams.get("limit") || "10",
    };
  
    // Add other parameters only if they have values
    if (data?.query !== undefined ? data.query : searchParams.get("query")) {
      params.query = data?.query !== undefined ? data.query : searchParams.get("query");
    }
    if (data?.startDate !== undefined ? data.startDate : searchParams.get("startDate")) {
      params.startDate = data?.startDate !== undefined ? data.startDate : searchParams.get("startDate");
    }
    if (data?.attended !== undefined ? data.attended : searchParams.get("attended")) {
      params.attended = data?.attended !== undefined ? data.attended : searchParams.get("attended");
    }
    
    // Preserve sorting unless explicitly changed
    if (data?.sortField !== undefined ? data.sortField : searchParams.get("sortField")) {
      params.sortField = data?.sortField !== undefined ? data.sortField : searchParams.get("sortField");
    }
    if (data?.sortOrder !== undefined ? data.sortOrder : searchParams.get("sortOrder")) {
      params.sortOrder = data?.sortOrder !== undefined ? data.sortOrder : searchParams.get("sortOrder");
    }
  
    const queryParams = new URLSearchParams(params);
    navigate(`/company/startups?${queryParams.toString()}`);
  };

  const handleSortChange = (sortField: string, sortOrder: string) => {
    handleSearchParamsChange({
      ...Object.fromEntries(searchParams.entries()),
      sortField: sortField || '',
      sortOrder: sortOrder || '',
      page: 1,
    });
  };

  return (
    <div>
      <PageHeader title="Startups">
        <Form
          form={form}
          onValuesChange={(_, allData) =>
            handleSearchParamsChange({ ...allData, page: 1 })
          }
          initialValues={{
            query: searchParams.get("query"),
            startDate:
              searchParams.get("startDate") &&
              dayjs(searchParams.get("startDate")),
            attended: searchParams.get("attended"),
          }}
        >
          <div className="AdminHeader-extra">
            <Form.Item name="query">
              <Input placeholder="Search" allowClear size="middle" />
            </Form.Item>
            <Form.Item name="startDate">
              <DatePicker placeholder="Date" size="middle" />
            </Form.Item>
            <Form.Item name="attended">
              <Select
                allowClear
                placeholder="Attendance"
                style={{ minWidth: 150 }}
              >
                <Select.Option value="true">Attended</Select.Option>
                <Select.Option value="false">Not Attended</Select.Option>
              </Select>
            </Form.Item>
            {Array.isArray(user.access[Events?.defult?.id]) &&
              user.access[Events?.defult?.id].includes(8) ? null : 
              <>
                <Button
                  type="primary"
                  onClick={() => setOpenForm(true)}
                  size="middle"
                >
                  ADD STARTUPS +
                </Button>
                <Button onClick={() => setFormModal(true)}>
                  UPLOAD <SiMicrosoftexcel size={20} color="green" />
                </Button>
                <Button
                  loading={isGeneratingExcel}
                  onClick={handleGenerateExcel}
                  size="middle"
                  style={{ background: "#fff", cursor: "pointer" }}
                >
                  EXPORT <SiMicrosoftexcel size={20} color="green" />
                </Button>
              </>
            }
          </div>
        </Form>
      </PageHeader>
      {loading ? (
        <Loading />
      ) : (
        <DataTable
          data={data}
          meta={meta}
          loading={isLoadingData}
          onPageChange={(page: number, pageSize: number) =>
            handleSearchParamsChange({ page, pageSize })
          }
          onChange={handleEditItem}
          delete={handleDeleteItem}
          updateAttendance={(data: any) => updateAttendance(data)}
          onSortChange={handleSortChange}
        />
      )}
      {openForm && (
        <Forms
          visible={openForm}
          data={selectedItem}
          onClose={() => {
            setSelectedItem({});
            setOpenForm(!openForm);
          }}
          onChange={fetchData}
        />
      )}
      {formModal && (
        <UploadModal
          open={formModal}
          close={() => setFormModal(false)}
          reload={fetchData}
        />
      )}
    </div>
  );
}

export default StartupScreen;